import { createServerFn } from "@tanstack/react-start";

import type { InboxDirection } from "@/features/inbox/filters";
import { listInboxes } from "@/features/connected-accounts/server";
import { getSenderIdentities } from "@/features/identities/server";
import {
  deleteInboxThread,
  downloadStoredAttachment,
  getInboxThread,
  listInboxThreadsPage,
  markInboxThreadRead,
  markInboxThreadUnread,
  sendEmail,
  uploadStoredAttachments,
} from "@/features/inbox/server";
import { mapInboxThread, mapInboxThreadsPage } from "@/features/inbox/view-model";

function passthroughValidator<T>(value: T) {
  return value;
}

export const getInboxListView = createServerFn({ method: "GET" })
  .validator(
    passthroughValidator<
      | {
          domainId?: string | null;
          aliasId?: string | null;
          direction?: InboxDirection | null;
          search?: string | null;
          includeBounce?: boolean | null;
          includeWarmup?: boolean | null;
        }
      | undefined
    >,
  )
  .handler(async ({ data }) => {
    const page = await listInboxThreadsPage({
      limit: 25,
      domainId: data?.domainId ?? undefined,
      aliasId: data?.aliasId ?? undefined,
      direction: data?.direction ?? undefined,
      search: data?.search ?? undefined,
    });
    return mapInboxThreadsPage(page).items;
  });

export const getInboxThreadsPage = createServerFn({ method: "GET" })
  .validator(
    passthroughValidator<
      | {
          domainId?: string | null;
          aliasId?: string | null;
          direction?: InboxDirection | null;
          search?: string | null;
          includeBounce?: boolean | null;
          includeWarmup?: boolean | null;
          cursor?: string | null;
          limit?: number | null;
        }
      | undefined
    >,
  )
  .handler(async ({ data }) => {
    const page = await listInboxThreadsPage({
      limit: data?.limit ?? 25,
      domainId: data?.domainId ?? undefined,
      aliasId: data?.aliasId ?? undefined,
      direction: data?.direction ?? undefined,
      search: data?.search ?? undefined,
      cursor: data?.cursor ?? undefined,
    });

    return mapInboxThreadsPage(page);
  });

export const getInboxThreadView = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ threadId: string }>)
  .handler(async ({ data }) => {
    return mapInboxThread(await getInboxThread(data.threadId));
  });

export const markThreadRead = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ threadId: string }>)
  .handler(async ({ data }) => {
    await markInboxThreadRead(data.threadId);
    return { success: true };
  });

export const markThreadUnread = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ threadId: string }>)
  .handler(async ({ data }) => {
    await markInboxThreadUnread(data.threadId);
    return { success: true };
  });

export const deleteThread = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ threadId: string }>)
  .handler(async ({ data }) => {
    await deleteInboxThread(data.threadId);
    return { success: true };
  });

export const getComposeOptions = createServerFn({ method: "GET" }).handler(async () => {
  const [senderIdentities, inboxes] = await Promise.all([
    getSenderIdentities(),
    listInboxes().catch(() => []),
  ]);

  const owned = senderIdentities.aliases.map((alias) => ({
    id: alias.id,
    email: alias.full_email,
    label: alias.full_email,
  }));
  // A connected mailbox (Gmail, Google Workspace) sends through its own provider and is not a
  // sender identity of a verified domain, so it joins the options from the inbox list.
  const connected = inboxes
    .filter((inbox) => inbox.provider !== "bavimail" && inbox.is_active)
    .filter((inbox) => !owned.some((option) => option.id === inbox.id))
    .map((inbox) => ({ id: inbox.id, email: inbox.full_email, label: inbox.full_email }));

  return [...owned, ...connected];
});

export const sendComposeEmail = createServerFn({ method: "POST" })
  .validator(
    passthroughValidator<{
      aliasId: string;
      toEmails: string[];
      ccEmails?: string[];
      bccEmails?: string[];
      subject: string;
      body: string;
      conversationId?: string | null;
      inReplyTo?: string | null;
      sendAt?: string | null;
      sendAtTimezone?: string | null;
      attachments?: Array<{
        attachmentId: string;
        contentId?: string | null;
      }>;
    }>,
  )
  .handler(async ({ data }) => {
    const email = await sendEmail({
      aliasId: data.aliasId,
      toEmails: data.toEmails,
      ccEmails: data.ccEmails?.length ? data.ccEmails : null,
      bccEmails: data.bccEmails?.length ? data.bccEmails : null,
      subject: data.subject,
      html: data.body,
      conversationId: data.conversationId ?? null,
      inReplyTo: data.inReplyTo ?? null,
      sendAt: data.sendAt ?? null,
      sendAtTimezone: data.sendAtTimezone ?? null,
      attachments: data.attachments?.length ? data.attachments : null,
    });

    return {
      id: email.id,
      conversationId: email.thread_id,
    };
  });

export const downloadAttachmentAction = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ attachmentId: string }>)
  .handler(async ({ data }) => {
    const buffer = await downloadStoredAttachment(data.attachmentId);
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  });

export const uploadComposeAttachments = createServerFn({ method: "POST" })
  .validator((input: FormData) => input)
  .handler(async ({ data }) => {
    const files = data.getAll("files").filter((value): value is File => value instanceof File);
    return await uploadStoredAttachments(files);
  });
